import { useRef, useState } from 'react';
import {
  Button,
  Callout,
  EmptyState,
  Icon,
  Modal,
  Skeleton,
  Stack,
  Table,
  Textarea,
  icons,
  theme,
  type TableColumn,
} from '@agentisai/design-system';
import {
  MAX_EVAL_TEXT_DOCUMENT_BYTES,
  UPLOAD_ACCEPT,
  evalDocumentContentType,
  isEvalDocumentName,
  type EvalDocument,
} from '@agentisai/shared-types';
import { ApiError } from '../../queries/client';
import {
  useDeleteEvalDocument,
  useEvalDocumentText,
  useEvalDocuments,
  useSaveEvalDocumentText,
  useUploadEvalDocument,
} from '../../queries/useEvalFixtures';
import { formatBytes, muted } from './format';

const isText = (name: string) => evalDocumentContentType(name)?.startsWith('text/') ?? false;

function conflictOr(err: unknown, fallback: string): string {
  return err instanceof ApiError && err.status === 409
    ? 'Someone changed this document since you opened it. Close and reopen it to get the latest copy.'
    : fallback;
}

function EditDocument({ name, onClose }: { name: string; onClose: () => void }) {
  const doc = useEvalDocumentText(name, true);
  const save = useSaveEvalDocumentText();
  // Local draft (R1.0): null until the user types, so the loaded text shows as-is.
  const [draft, setDraft] = useState<string | null>(null);
  const loaded = doc.data?.data;
  const text = draft ?? loaded?.text ?? '';
  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit ${name}`}
      size="lg"
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!loaded || draft === null || save.isPending}
            loading={save.isPending}
            onClick={() =>
              loaded &&
              save.mutate({ name, text, etag: loaded.etag }, { onSuccess: () => onClose() })
            }
          >
            Save
          </Button>
        </>
      }
    >
      {doc.isPending && <Skeleton shape="card" height={200} />}
      {doc.isError && <Callout tone="danger">Couldn't load {name}.</Callout>}
      {loaded && (
        <Textarea
          label="Content"
          value={text}
          onChange={(e) => setDraft(e.target.value)}
          rows={18}
          fullWidth
        />
      )}
      {save.isError && (
        <div role="alert" style={{ marginTop: theme.space[3] }}>
          <Callout tone="danger">{conflictOr(save.error, "Couldn't save the document.")}</Callout>
        </div>
      )}
    </Modal>
  );
}
EditDocument.displayName = 'EditDocument';

/**
 * Documents in the eval-fixtures bucket (ADR-0073) — what a case's
 * `setup.documents` attaches. Upload (same name replaces), edit text documents in
 * place, delete. The bucket is versioned, so a replace or delete can be undone
 * from S3. Write controls show only with `evals:write`; the BFF enforces it.
 */
export function EvalDocumentsPanel({ canWrite }: { canWrite: boolean }) {
  const documents = useEvalDocuments(true);
  const upload = useUploadEvalDocument();
  const remove = useDeleteEvalDocument();
  const fileInput = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [badName, setBadName] = useState<string | null>(null);

  function onPick(file: File | undefined) {
    if (!file) return;
    if (!isEvalDocumentName(file.name)) {
      setBadName(file.name);
      return;
    }
    setBadName(null);
    upload.mutate(file);
  }

  const columns: TableColumn<EvalDocument>[] = [
    { key: 'name', header: 'Document' },
    { key: 'sizeBytes', header: 'Size', render: (d) => formatBytes(d.sizeBytes) },
    {
      key: 'lastModified',
      header: 'Updated',
      render: (d) => new Date(d.lastModified).toLocaleString('en-AU'),
    },
    ...(canWrite
      ? [
          {
            key: 'actions',
            header: 'Actions',
            ariaLabel: 'Document actions',
            render: (d: EvalDocument) => (
              <Stack direction="row" gap={1}>
                {isText(d.name) && d.sizeBytes <= MAX_EVAL_TEXT_DOCUMENT_BYTES && (
                  <Button
                    variant="link"
                    size="sm"
                    aria-label={`Edit ${d.name}`}
                    onClick={() => setEditing(d.name)}
                  >
                    Edit
                  </Button>
                )}
                <Button
                  variant="link"
                  size="sm"
                  aria-label={`Delete ${d.name}`}
                  onClick={() => setConfirmDelete(d.name)}
                >
                  Delete
                </Button>
              </Stack>
            ),
          } satisfies TableColumn<EvalDocument>,
        ]
      : []),
  ];

  return (
    <Stack direction="column" gap={4}>
      <Stack direction="row" gap={3} align="center" justify="between" wrap>
        <span style={muted}>
          Files a case can attach by name. Markdown, text, CSV or PDF. Uploading a name that already
          exists replaces that file.
        </span>
        {canWrite && (
          <>
            {/* Uncontrolled file input (R25 allows the ref): the button opens it. */}
            <input
              ref={fileInput}
              type="file"
              accept={UPLOAD_ACCEPT}
              hidden
              aria-label="Choose a document to upload"
              onChange={(e) => {
                onPick(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <Button
              variant="secondary"
              leftIcon={<Icon as={icons.Plus} size="sm" />}
              loading={upload.isPending}
              disabled={upload.isPending}
              onClick={() => fileInput.current?.click()}
            >
              Upload document
            </Button>
          </>
        )}
      </Stack>

      {badName && (
        <div role="alert">
          <Callout tone="danger">
            “{badName}” can't be used as a document name. Use letters, numbers, dots, dashes or
            underscores, with a .md, .txt, .csv or .pdf extension.
          </Callout>
        </div>
      )}
      {upload.isError && (
        <div role="alert">
          <Callout tone="danger">The upload failed. Try again.</Callout>
        </div>
      )}
      {remove.isError && (
        <div role="alert">
          <Callout tone="danger">Couldn't delete the document.</Callout>
        </div>
      )}

      {documents.isPending && <Skeleton shape="card" height={160} />}
      {documents.isError && (
        <div role="alert">
          <Callout tone="danger">Couldn't load documents. Reload the page to try again.</Callout>
        </div>
      )}
      {documents.isSuccess &&
        (documents.data.data.documents.length === 0 ? (
          <EmptyState
            icon={<Icon as={icons.FileText} size="lg" />}
            title="No documents yet"
            description="Upload a document, then attach it to a case in a question set."
          />
        ) : (
          <Table
            caption="Eval documents"
            rows={documents.data.data.documents}
            columns={columns}
            getRowId={(d) => d.name}
            density="compact"
          />
        ))}

      {editing && <EditDocument name={editing} onClose={() => setEditing(null)} />}
      {confirmDelete && (
        <Modal
          open
          onClose={() => setConfirmDelete(null)}
          title={`Delete ${confirmDelete}?`}
          size="sm"
          actions={
            <>
              <Button variant="ghost" onClick={() => setConfirmDelete(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                loading={remove.isPending}
                disabled={remove.isPending}
                onClick={() =>
                  remove.mutate(confirmDelete, { onSettled: () => setConfirmDelete(null) })
                }
              >
                Delete
              </Button>
            </>
          }
        >
          Cases that attach this document will fail until it is uploaded again. The bucket keeps the
          previous version, so it can be restored.
        </Modal>
      )}
    </Stack>
  );
}
EvalDocumentsPanel.displayName = 'EvalDocumentsPanel';
